/**
 * Marque ProspectLab — symbole « Ascension » (trois colonnes + sphère bronze).
 *
 * Trois composants, une seule géométrie :
 *   <Logo>      le symbole seul, dessiné dans sa boîte de référence 132 × 104 ;
 *   <Wordmark>  « Prospect » gras + « Lab » régulier, aux couleurs de la charte ;
 *   <Lockup>    les deux assemblés, écart de 18 px comme le kit.
 *
 * `variant` désigne la SURFACE, pas le symbole : "light" = les remplissages
 * prévus pour un fond clair (colonnes bleu profond), "dark" = ceux prévus pour
 * un fond sombre (colonnes claires), "mono" = un aplat en `currentColor`, pour
 * l'impression, les filigranes et l'icône épinglée de Safari.
 *
 * Les dégradés sont posés en `userSpaceOnUse` avec des extrémités calculées :
 * un `linear-gradient(100deg)` CSS se calcule dans la géométrie réelle de la
 * boîte, et les trois colonnes n'ont pas la même hauteur — en repère normalisé
 * l'inclinaison différerait de l'une à l'autre. Les valeurs viennent de
 * `frontend/scripts/build_brand_assets.py`, qui produit aussi les SVG, les
 * favicons et l'image de partage depuis cette même géométrie.
 *
 * `size` est la HAUTEUR rendue ; la largeur suit le rapport 132/104 de la
 * boîte. La sphère est à fleur des bords haut et droit : ne jamais enfermer le
 * symbole dans un carré qui la rognerait.
 */
import { useId } from "react";
import { cn } from "@/lib/utils";

const BOX_W = 132;
const BOX_H = 104;
const RATIO = BOX_W / BOX_H;

// Tracés des colonnes : coins hauts arrondis (12), coins bas presque droits (4).
const PILLARS = [
  "M6,70a12,12 0 0 1 12,-12h0a12,12 0 0 1 12,12v30a4,4 0 0 1 -4,4h-16a4,4 0 0 1 -4,-4z",
  "M39,44a12,12 0 0 1 12,-12h0a12,12 0 0 1 12,12v56a4,4 0 0 1 -4,4h-16a4,4 0 0 1 -4,-4z",
  "M72,20a12,12 0 0 1 12,-12h0a12,12 0 0 1 12,12v80a4,4 0 0 1 -4,4h-16a4,4 0 0 1 -4,-4z",
];
const GRADIENT_LINES = [
  { x1: 2.43, y1: 78.25, x2: 33.57, y2: 83.75 },
  { x1: 33.21, y1: 64.86, x2: 68.79, y2: 71.14 },
  { x1: 64.15, y1: 52.5, x2: 103.85, y2: 59.5 },
];
const MONO_OPACITY = [0.55, 0.75, 1];

// Remplissages convertis d'oklch() en sRGB à la fabrication : oklch n'est pas
// lu par Safari et Edge anciens, et un logo n'a pas le droit de tomber en
// noir sur un navigateur de retard.
const FILLS = {
  light: {
    pillars: [["#647d97", "#233447"], ["#7089a4", "#283a4c"], ["#7b95b0", "#2d3f52"]],
    sphere: [[0, "#ffffff"], [0.3, "#e6a973"], [0.7, "#ac6820"], [1, "#6d411c"]],
  },
  dark: {
    pillars: [["#8da8c3", "#435a73"], ["#a0bbd7", "#4d667f"], ["#b8cde4", "#59718b"]],
    sphere: [[0, "#ffffff"], [0.32, "#ecaf79"], [0.74, "#b26e27"], [1, "#7d4a1e"]],
  },
};

// Couleur du « Lab » : acier moyen sur fond clair, acier clair sur fond sombre.
export const BRAND_STEEL_MID = "#486079";
export const BRAND_STEEL_LIGHT = "#a6c1dd";
export const BRAND_INK = "#16171a";

export default function Logo({ size = 32, variant = "light", className = "",
                              title = "ProspectLab" }) {
  const uid = useId().replace(/:/g, "");
  const mono = variant === "mono";
  const fills = FILLS[variant] || FILLS.light;
  return (
    <svg
      width={Math.round(size * RATIO)} height={size}
      viewBox={`0 0 ${BOX_W} ${BOX_H}`} className={className}
      role="img" aria-label={title}
    >
      {!mono && (
        <defs>
          {GRADIENT_LINES.map((line, i) => (
            <linearGradient key={i} id={`${uid}p${i}`} gradientUnits="userSpaceOnUse" {...line}>
              <stop offset="0" stopColor={fills.pillars[i][0]} />
              <stop offset="1" stopColor={fills.pillars[i][1]} />
            </linearGradient>
          ))}
          {/* radial-gradient(circle at 32% 28%) : foyer décalé, rayon « farthest-corner ». */}
          <radialGradient id={`${uid}s`} gradientUnits="userSpaceOnUse"
                          cx="106.24" cy="8.96" r="31.69">
            {fills.sphere.map(([offset, color]) => (
              <stop key={offset} offset={offset} stopColor={color} />
            ))}
          </radialGradient>
        </defs>
      )}
      {PILLARS.map((d, i) => (
        <path key={i} d={d}
              fill={mono ? "currentColor" : `url(#${uid}p${i})`}
              opacity={mono ? MONO_OPACITY[i] : undefined} />
      ))}
      <circle cx="112" cy="16" r="16" fill={mono ? "currentColor" : `url(#${uid}s)`} />
    </svg>
  );
}

/** Le mot-clé seul — « Lab » en graisse régulière, jamais en gras. */
export function Wordmark({ variant = "light", className = "" }) {
  return (
    <span className={cn("font-brand font-bold tracking-[-0.015em]", className)}>
      Prospect
      <span className="font-normal"
            style={{ color: variant === "dark" ? BRAND_STEEL_LIGHT : BRAND_STEEL_MID }}>
        Lab
      </span>
    </span>
  );
}

/**
 * Symbole + mot-clé. `size` est la hauteur du symbole ; l'écart de 18 px et le
 * corps de 28 px du kit sont proportionnés à partir d'elle, pour que le lockup
 * garde ses proportions à toutes les tailles.
 */
export function Lockup({ size = 44, variant = "light", className = "", textClassName = "" }) {
  // Métriques du kit rapportées à la hauteur du symbole : écart 18 px et corps
  // 28 px pour un symbole de 43,68 px de haut (132 × 104 à 42 %). Le corps est
  // posé sur le conteneur, le mot-clé en hérite.
  return (
    <span className={cn("inline-flex items-center", className)}
          style={{ gap: `${(size * 18) / 43.68}px`, fontSize: `${(size * 28) / 43.68}px` }}>
      <Logo size={size} variant={variant} className="shrink-0" />
      <Wordmark variant={variant} className={cn("whitespace-nowrap", textClassName)} />
    </span>
  );
}
